/**
 * TelemetryDeck event type constants and page name mapping.
 *
 * Usage: import { EVENTS, getPageName } from '@constants/telemetryEvents'
 *
 * Convention: Domain.ActionOrState
 *   - Domain is the feature area (Auth, Enrollment, Classes, etc.)
 *   - ActionOrState is what happened (PageView, LoginSuccess, etc.)
 */

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export const EVENTS = {
  // ── Navigation ────────────────────────────────────────────────────────────
  /** Fired automatically on every route change by RouteTracker. */
  PAGE_VIEW: 'Navigation.PageView',

  // ── Landing page ──────────────────────────────────────────────────────────
  /**
   * Landing page loaded. Payload: { isLoggedIn: 'true'|'false' }.
   * Use this to answer "are logged-in users landing here instead of the dashboard?"
   */
  LANDING_VIEWED: 'Landing.Viewed',
  /**
   * User scrolled a landing section into view. Payload: { section }.
   * Shows which sections (about, mission, courses, how-to-join, etc.) people actually read.
   */
  LANDING_SECTION_VIEWED: 'Landing.SectionScrolledIntoView',
  /** "Learn More" button in the hero was clicked (scrolls to about section). */
  LANDING_HERO_LEARN_MORE: 'Landing.HeroLearnMoreClicked',
  /** "Schedule a Visit" button in the hero was clicked. */
  LANDING_HERO_SCHEDULE_VISIT: 'Landing.HeroScheduleVisitClicked',
  /**
   * "Schedule a Visit" modal opened. Payload: { source: 'hero'|'contact-section' }.
   * Shows which CTA placement drives the most visit requests.
   */
  LANDING_SCHEDULE_VISIT_OPENED: 'Landing.ScheduleVisitModalOpened',
  /** Schedule-a-visit form submitted successfully. */
  LANDING_SCHEDULE_VISIT_SUBMITTED: 'Landing.ScheduleVisitSubmitted',
  /** Landing contact form submitted successfully. */
  LANDING_CONTACT_FORM_SUBMITTED: 'Landing.ContactFormSubmitted',
  /** Visitor dismissed a landing-page site announcement banner. */
  LANDING_ANNOUNCEMENT_DISMISSED: 'Landing.AnnouncementDismissed',
  /**
   * Signed-in member opened the public landing preview from the account menu.
   * Payload: { source: 'profile-menu'|'mobile-profile' }.
   */
  LANDING_PUBLIC_SITE_OPENED: 'Landing.PublicSiteOpened',

  // ── Auth ──────────────────────────────────────────────────────────────────
  /** User submitted the login form (before API response). Payload: { persistSession: 'yes'|'no' }. */
  LOGIN_ATTEMPTED: 'Auth.LoginAttempted',
  /** Login credentials were rejected. Payload: { reason }. */
  LOGIN_FAILED: 'Auth.LoginFailed',
  /**
   * Login succeeded and 2FA challenge appeared.
   * Payload: { method: 'totp'|'sms' }.
   */
  TWO_FACTOR_TRIGGERED: 'Auth.TwoFactorChallengeShown',
  /** 2FA code accepted — full login complete. Payload: { method, rememberDevice: 'yes'|'no' }. */
  TWO_FACTOR_COMPLETED: 'Auth.TwoFactorCompleted',
  /** 2FA code rejected. */
  TWO_FACTOR_FAILED: 'Auth.TwoFactorFailed',
  /**
   * Authenticated session established (access token present).
   * Fired by TelemetryUserSync when session transitions from none → user+token.
   * Does not fire for registration stubs or pending verification without a token.
   * Payload: { role }.
   */
  LOGIN_SUCCESS: 'Auth.LoginSuccess',
  /** User explicitly logged out (or session cleared after an authenticated session). */
  LOGOUT: 'Auth.Logout',
  /**
   * Idle timeout signed the user out while the tab was open.
   * Payload: { role }.
   */
  SESSION_IDLE_TIMEOUT: 'Auth.SessionIdleTimeout',
  /**
   * New public-site account created (API isNewRegistration).
   * Payload: { role: 'parent', onboardingSource: 'public-site' }.
   */
  REGISTER_COMPLETED: 'Auth.RegisterCompleted',
  /** Registration API call failed. Payload: { reason }. */
  REGISTER_FAILED: 'Auth.RegisterFailed',
  /** Registration email code accepted. Payload: { onboardingSource }. */
  EMAIL_VERIFIED: 'Auth.EmailVerified',
  EMAIL_VERIFY_RESENT: 'Auth.EmailVerifyResent',
  /** Registration phone code accepted. Payload: { onboardingSource }. */
  PHONE_VERIFIED: 'Auth.PhoneVerified',
  DASHBOARD_OVERVIEW_STAT_OPENED: 'Dashboard.OverviewStatOpened',
  /** Parent clicked a getting-started CTA on the family dashboard widget. */
  DASHBOARD_FAMILY_NEXT_STEP_CLICKED: 'Dashboard.FamilyNextStepClicked',
  /** User left the completion screen and finished the full onboarding flow. Payload: { onboardingSource }. */
  ONBOARDING_COMPLETE: 'Auth.OnboardingComplete',
  /** User requested a password reset email. */
  FORGOT_PASSWORD: 'Auth.ForgotPasswordRequested',
  /** User completed 2FA setup. Payload: { method: 'totp'|'sms', onboardingSource }. */
  TWO_FACTOR_SETUP: 'Auth.TwoFactorSetupCompleted',

  // ── Enrollment (family journey) ───────────────────────────────────────────
  /**
   * Family clicked the "Enroll" button opening the child-selection modal.
   * Payload: { classId }.
   * Funnel step 1: interest confirmed.
   */
  ENROLL_MODAL_OPENED: 'Enrollment.EnrollModalOpened',
  /**
   * Enrollment API call succeeded — child is enrolled.
   * Payload: { classId, childId }.
   * Funnel step 2: conversion complete.
   */
  ENROLL_COMPLETED: 'Enrollment.EnrollmentCompleted',
  /**
   * Tutor applied or updated a per-student tuition discount on the roster.
   * Payload: { classId, enrollmentId, amount }.
   */
  ENROLLMENT_TUITION_DISCOUNT_SET: 'Enrollment.TuitionDiscountSet',
  /**
   * Tutor or admin removed a student from a class roster (Payments tab).
   * Payload: { classId, enrollmentId }.
   */
  ROSTER_STUDENT_REMOVED: 'Roster.StudentRemoved',
  /**
   * Tutor or admin opened payment history for a roster student (Payments tab).
   * Payload: { classId, enrollmentId }.
   */
  ROSTER_PAYMENT_HISTORY_OPENED: 'Roster.PaymentHistoryOpened',
  /**
   * Tutor or admin removed a payment ledger row recorded in error.
   * Payload: { classId, enrollmentId, paymentId }.
   */
  ROSTER_PAYMENT_REMOVED: 'Roster.PaymentRemoved',
  /**
   * Visitor clicked the pay button (Venmo/PayPal link) on a tutor profile.
   * Payload: { tutorUserId, method }.
   */
  TUTOR_PROFILE_PAYMENT_LINK_CLICKED: 'Enrollment.TutorPaymentLinkClicked',
  /**
   * Visitor opened a class page from the tutor profile class list.
   * Payload: { tutorUserId, classId }.
   */
  TUTOR_PROFILE_CLASS_OPENED: 'Enrollment.TutorProfileClassOpened',

  // ── Family registration ───────────────────────────────────────────────────
  /** Family successfully submitted their registration. */
  FAMILY_REGISTRATION_SUBMITTED: 'Family.RegistrationSubmitted',
  /** Admin marked a family registration fee as paid (cash). */
  FAMILY_REGISTRATION_MARKED_PAID: 'Family.RegistrationMarkedPaid',
  /** Admin removed a family registration payment. */
  FAMILY_REGISTRATION_PAYMENT_REMOVED: 'Family.RegistrationPaymentRemoved',
  /** Admin applied a full yearly-fee discount on a family registration. */
  FAMILY_REGISTRATION_DISCOUNT_APPLIED: 'Family.RegistrationDiscountApplied',
  /** Admin removed a yearly-fee discount from a family registration. */
  FAMILY_REGISTRATION_DISCOUNT_REMOVED: 'Family.RegistrationDiscountRemoved',

  // ── Classes (tutor workflow) ──────────────────────────────────────────────
  /**
   * Tutor's class draft was saved (first time, via "Create" form).
   * Payload: { classId }.
   */
  CLASS_CREATED: 'Classes.DraftCreated',
  /**
   * Tutor submitted a class for admin review for the first time.
   * Payload: { classId }.
   */
  CLASS_SUBMITTED: 'Classes.SubmittedForReview',
  /**
   * Tutor re-submitted a previously rejected / change-requested class.
   * Payload: { classId }.
   */
  CLASS_RESUBMITTED: 'Classes.Resubmitted',

  // ── Classes (admin review workflow) ──────────────────────────────────────
  /**
   * Admin/board approved a class.
   * Payload: { classId }.
   */
  CLASS_APPROVED: 'Classes.Approved',
  /**
   * Admin/board rejected a class.
   * Payload: { classId }.
   */
  CLASS_REJECTED: 'Classes.Rejected',
  /**
   * Admin/board requested changes (class goes back to tutor for edits).
   * Payload: { classId }.
   */
  CLASS_CHANGES_REQUESTED: 'Classes.ChangesRequested',
  /**
   * Tutor withdrew a draft/submitted class.
   * Payload: { classId }.
   */
  CLASS_WITHDRAWN: 'Classes.Withdrawn',
  /**
   * Tutor soft-deleted a withdrawn class from My Classes.
   * Payload: { classId }.
   */
  CLASS_DELETED: 'Classes.WithdrawnClassDeleted',

  // ── Reports ───────────────────────────────────────────────────────────────
  /**
   * Admin exported a report as CSV.
   * Payload: { reportId, exportType, rowCount }.
   * Shows which reports are actually used vs visited.
   */
  REPORT_EXPORTED: 'Reports.CsvExported',
  /**
   * Admin exported a report as PDF.
   * Payload: { reportId, exportType, rowCount }.
   */
  REPORT_PDF_EXPORTED: 'Reports.PdfExported',
  /**
   * Leadership changed sort on the Semester financial report (By class, By tutor, or By family).
   * Payload: { table: 'class'|'tutor'|'family', sort }.
   */
  REPORT_SEMESTER_FINANCIAL_SORTED: 'Reports.SemesterFinancialSorted',

  // ── Attendance ────────────────────────────────────────────────────────────
  /**
   * Tutor saved an attendance session.
   * Payload: { classId, sessionDate, studentCount }.
   * Shows whether tutors are actually using the attendance feature.
   */
  ATTENDANCE_SAVED: 'Attendance.Saved',
  /**
   * Leadership opened a class roster from a semester class list (cover / attendance help).
   * Payload: { classId, source: 'semester-classes' | 'semester-detail' }.
   */
  ROSTER_OPENED_FROM_SEMESTER: 'Classes.RosterOpenedFromSemester',
  /**
   * Leadership opened a class roster from a user profile Children list.
   * Payload: { classId, userId }.
   */
  ROSTER_OPENED_FROM_USER_PROFILE: 'Classes.RosterOpenedFromUserProfile',

  // ── Announcements ─────────────────────────────────────────────────────────
  /**
   * Admin published an announcement.
   * Payload: { sendEmail: 'true'/'false' }.
   * Shows how often email vs banner-only announcements are sent.
   */
  ANNOUNCEMENT_CREATED: 'Announcements.Created',
  /** Admin deleted/removed an active announcement. */
  ANNOUNCEMENT_DELETED: 'Announcements.Deleted',

  // ── User management (admin) ───────────────────────────────────────────────
  /** Admin approved a pending user registration. */
  USER_APPROVED: 'Admin.UserApproved',
  /** Admin rejected a pending user registration. */
  USER_REJECTED: 'Admin.UserRejected',
  /** Admin reactivated an inactive user. */
  USER_ACTIVATED: 'Admin.UserActivated',
  /** Admin deactivated an active user. */
  USER_DEACTIVATED: 'Admin.UserDeactivated',
  /**
   * Admin sent an invitation.
   * Payload: { role }.
   * Shows whether the invite flow is being used and for which roles.
   */
  USER_INVITED: 'Admin.UserInvited',
  /**
   * Admin imported emails to flag returning families.
   * Payload: { flaggedCount, alreadyFlaggedCount, notFoundCount }.
   */
  RETURNING_FAMILIES_IMPORTED: 'Admin.ReturningFamiliesImported',
  /**
   * Admin cleared all returningFamily flags (after first-semester bootstrap).
   * Payload: { clearedCount }.
   */
  RETURNING_FAMILIES_CLEARED: 'Admin.ReturningFamiliesCleared',

  // ── Semesters ─────────────────────────────────────────────────────────────
  /** Admin created a new semester. */
  SEMESTER_CREATED: 'Semesters.Created',
  /** Admin updated semester details (name, dates, rooms, etc.). */
  SEMESTER_UPDATED: 'Semesters.Updated',
  /**
   * Semester Detail hero showed a seasonal theme derived from the semester name.
   * Payload: { season } — spring | summer | fall | winter.
   */
  SEMESTER_SEASON_THEME_SHOWN: 'Semester.SeasonThemeShown',

  // ── Notifications ─────────────────────────────────────────────────────────
  /**
   * User opened the notification panel.
   * Payload: { unreadCount }.
   * Shows whether notifications are being seen.
   */
  NOTIFICATIONS_OPENED: 'Notifications.PanelOpened',
  /** User clicked "Mark all read". */
  NOTIFICATIONS_ALL_READ: 'Notifications.AllMarkedRead',
  /** User clicked "Clear all" to dismiss all notifications. */
  NOTIFICATIONS_CLEARED: 'Notifications.AllCleared',

  // ── Help center ───────────────────────────────────────────────────────────
  /**
   * User typed a search query (fires when query crosses 3+ chars).
   * Payload: { resultCount }.
   * Shows whether help content is findable.
   */
  HELP_SEARCHED: 'Help.Searched',
  /**
   * User opened a help article.
   * Payload: { articleId, category, level: 'overview'|'reference' }.
   * Shows which articles are most useful.
   */
  HELP_ARTICLE_VIEWED: 'Help.ArticleViewed',

  // ── Settings ──────────────────────────────────────────────────────────────
  /**
   * User toggled light/dark theme.
   * Payload: { theme: 'light'|'dark' } — the NEW theme after the toggle.
   */
  SETTINGS_THEME_TOGGLED: 'Settings.ThemeToggled',
  /** Super admin changed address autocomplete provider. Payload: { provider: 'google'|'geoapify' }. */
  SETTINGS_ADDRESS_PROVIDER_CHANGED: 'Settings.AddressProviderChanged',
  /**
   * Super admin saved the co-op-wide class deposit amount.
   * Payload: { amount } e.g. "$20".
   */
  SETTINGS_CLASS_DEPOSIT_SAVED: 'Settings.ClassDepositSaved',
  /** User saved their profile (name, phone, address). */
  SETTINGS_PROFILE_SAVED: 'Settings.ProfileSaved',
  /** User changed their password via Account Settings. */
  SETTINGS_PASSWORD_CHANGED: 'Settings.PasswordChanged',
  /** User removed their profile photo. */
  SETTINGS_PHOTO_REMOVED: 'Settings.PhotoRemoved',
  /** User saved notification preference changes. */
  SETTINGS_NOTIFICATIONS_SAVED: 'Settings.NotificationsSaved',

  // ── Waitlist ───────────────────────────────────────────────────────────────
  /** Parent joined the waitlist for a class (class was full, waitlist enabled). */
  WAITLIST_JOINED: 'Waitlist.Joined',
  /** Tutor enabled the waitlist for a class. */
  WAITLIST_ENABLED: 'Waitlist.Enabled',
  /** Tutor disabled the waitlist for a class. */
  WAITLIST_DISABLED: 'Waitlist.Disabled',
  /**
   * Tutor moved a student from the waitlist to the active roster.
   * Payload: { classId, enrollmentId }.
   */
  WAITLIST_STUDENT_ACTIVATED: 'Waitlist.StudentActivated',
  /**
   * Tutor ended the waitlist (closed it, notified all waitlisted families).
   * Payload: { classId }.
   */
  WAITLIST_ENDED: 'Waitlist.Ended',

  // Paginated lists (users, registrations, review classes, audit log)
  LIST_PAGE_CHANGED: 'Lists.PageChanged',
} as const;

export type TelemetryEvent = (typeof EVENTS)[keyof typeof EVENTS];

// ---------------------------------------------------------------------------
// Route → human-readable page name
// Used by RouteTracker to enrich PAGE_VIEW signals.
// Entries are checked in order; first match wins, so list specifics first.
// ---------------------------------------------------------------------------

const ROUTE_NAMES: Array<[pattern: RegExp, name: string]> = [
  // Auth & onboarding
  [/^\/login$/, 'Login'],
  [/^\/register$/, 'Register'],
  [/^\/verify-email$/, 'VerifyEmail'],
  [/^\/verify-phone$/, 'VerifyPhone'],
  [/^\/forgot-password$/, 'ForgotPassword'],
  [/^\/reset-password$/, 'ResetPassword'],
  [/^\/confirm-email-change$/, 'ConfirmEmailChange'],
  [/^\/complete-invitation$/, 'CompleteInvitation'],
  [/^\/setup-2fa$/, 'Setup2FA'],
  [/^\/onboarding-complete$/, 'OnboardingComplete'],
  [/^\/terms$/, 'TermsOfService'],
  [/^\/privacy$/, 'PrivacyPolicy'],

  // Dashboards
  [/^\/dashboard$/, 'Dashboard'],
  [/^\/family$/, 'FamilyDashboard'],

  // Enrollment
  [/^\/enroll\/browse$/, 'Enrollment.Browse'],
  [/^\/enroll\/class\//, 'Enrollment.ClassDetail'],
  [/^\/enroll\/tutor\//, 'Enrollment.TutorProfile'],
  [/^\/enroll$/, 'Enrollment.Home'],

  // Family registration
  [/^\/family-registration/, 'FamilyRegistration'],

  // Users
  [/^\/users\/[^/]+$/, 'UserProfile'],
  [/^\/users$/, 'Users'],

  // Roles
  [/^\/roles\/[^/]+\/edit$/, 'RoleEdit'],
  [/^\/roles\/new$/, 'RoleCreate'],
  [/^\/roles$/, 'Roles'],

  // Semesters
  [/^\/semesters\/[^/]+\/classes$/, 'SemesterClasses'],
  [/^\/semesters\/[^/]+$/, 'SemesterDetail'],
  [/^\/semesters$/, 'Semesters'],

  // Classes
  [/^\/classes\/[^/]+\/roster$/, 'ClassRoster'],
  [/^\/classes\/[^/]+\/review$/, 'ClassReviewDetail'],
  [/^\/classes\/[^/]+\/edit$/, 'ClassEdit'],
  [/^\/classes\/review$/, 'ClassReview'],
  [/^\/classes\/new$/, 'ClassCreate'],
  [/^\/classes$/, 'Classes'],

  // Reports — specific routes first so /reports/xyz matches before /reports
  [/^\/reports\/attendance-per-class$/, 'Report.AttendancePerClass'],
  [/^\/reports\/bhg-full-attendance$/, 'Report.FullAttendance'],
  [/^\/reports\/location-by-zip$/, 'Report.LocationByZip'],
  [/^\/reports\/location-by-city$/, 'Report.LocationByCity'],
  [/^\/reports\/churches$/, 'Report.Churches'],
  [/^\/reports\/special-notes-families$/, 'Report.SpecialNotesFamilies'],
  [/^\/reports\/children-allergies$/, 'Report.ChildrenAllergies'],
  [/^\/reports\/emergency-contacts$/, 'Report.EmergencyContacts'],
  [/^\/reports\/children-tutor-notes$/, 'Report.ChildrenTutorNotes'],
  [/^\/reports\/enrollment-by-grade$/, 'Report.EnrollmentByGrade'],
  [/^\/reports\/class-capacity$/, 'Report.ClassCapacity'],
  [/^\/reports\/registration-completion$/, 'Report.RegistrationCompletion'],
  [/^\/reports\/attendance-rate-summary$/, 'Report.AttendanceRateSummary'],
  [/^\/reports\/outstanding-payments$/, 'Report.OutstandingPayments'],
  [/^\/reports\/semester-financial-report$/, 'Report.SemesterFinancial'],
  [/^\/reports\/semester-money$/, 'Report.SemesterFinancial'],
  [/^\/reports\/families-per-semester$/, 'Report.FamiliesPerSemester'],
  [/^\/reports\/new-accounts-by-month$/, 'Report.NewAccountsByMonth'],
  [/^\/reports\/login-visits$/, 'Report.LoginVisits'],
  [/^\/reports\/google-contacts-export$/, 'Report.GoogleContactsExport'],
  [/^\/reports$/, 'Reports'],

  // Other admin
  [/^\/audit$/, 'AuditLog'],
  [/^\/registrations$/, 'Registrations'],

  // Help
  [/^\/help\/[^/]+$/, 'HelpArticle'],
  [/^\/help$/, 'Help'],

  // Settings
  [/^\/settings\/account$/, 'Settings.Account'],
  [/^\/settings\/notifications$/, 'Settings.Notifications'],
  [/^\/settings\/announcements$/, 'Settings.Announcements'],
  [/^\/settings\/app$/, 'Settings.App'],

  // System / error
  [/^\/forbidden$/, 'Forbidden'],
  [/^\/unavailable$/, 'Unavailable'],

  // Public landing sections (real URLs so search can sitelink them)
  [/^\/about$/, 'Landing.About'],
  [/^\/mission$/, 'Landing.Mission'],
  [/^\/our-story$/, 'Landing.OurStory'],
  [/^\/our-board$/, 'Landing.OurBoard'],
  [/^\/courses$/, 'Landing.Courses'],
  [/^\/join$/, 'Landing.Join'],
  [/^\/resources$/, 'Landing.Resources'],
  [/^\/contact$/, 'Landing.Contact'],
  [/^\/$/, 'Landing'],
];

/**
 * Returns a human-readable page name for a given pathname.
 * Falls back to a sanitized version of the raw path for unknown routes.
 */
export function getPageName(pathname: string): string {
  for (const [pattern, name] of ROUTE_NAMES) {
    if (pattern.test(pathname)) {
      return name;
    }
  }
  return `Unknown(${pathname})`;
}
